import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@mui/material";
import { toast } from "react-toastify";
import API from "../../../api/axios";
import { APP_URLS } from "../../../api/url";
import NoRecord from "../../../assets/SharedComponents/Table/NoRecord";
import { ACTIVITY_PERIODS, buildQuery, formatDate, formatPercent, toastError } from "../helpers";
import { LoadingRow } from "./Shared";

const Stat = ({ label, value, tone }) => (
  <div className={`card-stat${tone ? ` ${tone}` : ""}`}>
    <div className="muted">{label}</div>
    <div className="value">{value}</div>
  </div>
);

const quotaUsage = (q) => (q ? `${q.used.toLocaleString()} / ${q.limit === null ? "∞" : q.limit.toLocaleString()}` : "-");

/**
 * GET /activity: per dealership over 24h / 7d / 30d — pushes, outcomes, failure rate, top error
 * codes, today's quota usage — and the pipeline right now (waiting, processing, stuck items).
 */
const ActivityTab = ({ access, subsidiaryId, onSubsidiaryChange, onShowPushes, onOpenQuotas, refreshToken }) => {
  const [period, _period] = useState("24h");
  // The filter that produced the stored report travels with it: a failed or slow request after a
  // filter change would otherwise leave the previous dealership's numbers on screen under the new
  // dealership's heading. Keeping the key instead of blanking on every load also means the
  // Refresh button does not clear the page while it reloads the same filter.
  const [report, _report] = useState(null);
  const [reportKey, _reportKey] = useState(null);
  const [loading, _loading] = useState(true);
  const seq = useRef(0);

  const filterKey = buildQuery({ period, "filter[subsidiary_id]": subsidiaryId });

  const load = useCallback(() => {
    const current = ++seq.current;
    const requested = buildQuery({ period, "filter[subsidiary_id]": subsidiaryId });
    _loading(true);
    API.get(`${APP_URLS.DEALER_API}/activity?${requested}`)
      .then((res) => {
        if (current !== seq.current) return;
        if (res.data.success === true) {
          _report(res.data.data);
          _reportKey(requested);
        } else {
          toast.error(res.data.message, { position: toast.POSITION.TOP_RIGHT });
        }
      })
      .catch((error) => current === seq.current && toastError(error))
      .finally(() => current === seq.current && _loading(false));
  }, [period, subsidiaryId]);

  // refreshToken: bumped by the page after quotas change elsewhere.
  useEffect(() => {
    load();
  }, [load, refreshToken]);

  // Only a report that belongs to the filter on screen is rendered.
  const shown = reportKey === filterKey ? report : null;
  const pipeline = shown?.pipeline;
  const failing = shown?.failing;
  const totals = shown?.totals;

  return (
    <>
      <div className="filters">
        {subsidiaryId && (
          <span className="muted">
            Dealership #{subsidiaryId}{" "}
            <button className="link" onClick={() => onSubsidiaryChange("")}>
              show all
            </button>
          </span>
        )}
        <select className="filter-select" value={period} onChange={(e) => _period(e.target.value)}>
          {ACTIVITY_PERIODS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.name}
            </option>
          ))}
        </select>
        <button className="btn btn-secondary" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>

      {pipeline && (
        <>
          <h5 className="text-start">Pipeline now</h5>
          <div className="cards">
            <Stat label="Listings waiting" value={pipeline.pending_items} />
            <Stat label="Being processed" value={pipeline.processing_items} />
            <Stat
              label={`Stuck (> ${pipeline.stuck_after_minutes} min)`}
              value={pipeline.stuck_items}
              tone={pipeline.stuck_items > 0 ? "danger" : ""}
            />
            <Stat label="Open pushes" value={pipeline.open_batches} />
            <Stat label="Oldest waiting since" value={formatDate(pipeline.oldest_pending_at)} />
          </div>
        </>
      )}

      {failing && failing.count > 0 && (
        <>
          <h5 className="text-start">
            Stuck listings{" "}
            <span className="muted">
              — every push has failed since their last success, for {failing.since_days}+ days
            </span>
          </h5>
          <p className="text-start muted">
            The ad stays online with its last good data, so a DMS that never reads its batch results shows no symptom.
            Usually a reference value that was removed, or a payload the dealer&apos;s system started sending wrong.
            {failing.truncated ? " Only the oldest are listed." : ""}
          </p>
          <div className="tableContainer">
            <table className="auctionTable">
              <thead className="tableHead">
                <tr className="table-row">
                  <th className="time-start">Dealership</th>
                  <th className="time-start">Listing</th>
                  <th className="time-start">Error</th>
                  <th className="time-start">Failing since</th>
                  <th className="time-start">Last try</th>
                  <th className="time-start">Attempts</th>
                  <th className="time-start">Actions</th>
                </tr>
              </thead>
              <tbody>
                {failing.listings.map((row) => (
                  <tr key={`failing-${row.subsidiary.id}-${row.external_reference}`} className="table-row">
                    <td>
                      <b>{row.subsidiary.name ?? "-"}</b>
                      <div className="muted">#{row.subsidiary.id}</div>
                    </td>
                    <td className="mono">{row.external_reference}</td>
                    <td>
                      <span className="mono">{row.error_code}</span>
                    </td>
                    <td className="text-danger fw-bold">{formatDate(row.failing_since)}</td>
                    <td>{formatDate(row.last_failed_at)}</td>
                    <td>{row.attempts}</td>
                    <td>
                      <div className="action-btns">
                        <Button variant="outlined" onClick={() => onShowPushes(String(row.subsidiary.id))}>
                          Pushes
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {totals && (
        <>
          <h5 className="text-start">
            {ACTIVITY_PERIODS.find((p) => p.value === shown.period)?.name} ({formatDate(shown.from)} → {formatDate(shown.to)})
          </h5>
          <div className="cards">
            <Stat label="Dealerships" value={totals.dealerships} />
            <Stat label="Pushes" value={totals.batches} />
            <Stat label="Listings" value={totals.listings} />
            <Stat label="Created / updated / unchanged" value={`${totals.created} / ${totals.updated} / ${totals.unchanged}`} />
            <Stat label="Failed" value={`${totals.failed} (${formatPercent(totals.failure_rate)})`} tone={totals.failed > 0 ? "warn" : ""} />
          </div>
          {shown.errors.length > 0 && (
            <p className="text-start">
              <b>Top errors:</b>{" "}
              {shown.errors.map((e) => (
                <span key={e.code} className="me-3">
                  <span className="mono">{e.code}</span> × {e.count}
                </span>
              ))}
            </p>
          )}
        </>
      )}

      <div className="tableContainer">
        <table className="auctionTable">
          <thead className="tableHead">
            <tr className="table-row">
              <th className="time-start">Dealership</th>
              <th className="time-start">Pushes / listings</th>
              <th className="time-start">Created / updated / unchanged</th>
              <th className="time-start">Failed</th>
              <th className="time-start">Top errors</th>
              <th className="time-start">Last push</th>
              <th className="time-start">Today: listings / photos</th>
              <th className="time-start">Actions</th>
            </tr>
          </thead>
          {loading ? (
            <LoadingRow colSpan={8} />
          ) : (
            <tbody>
              {shown?.dealerships?.length > 0 ? (
                shown.dealerships.map((row) => (
                  <tr key={`activity-${row.subsidiary.id}`} className="table-row">
                    <td>
                      <b>{row.subsidiary.name ?? "-"}</b>
                      <div className="muted">#{row.subsidiary.id}</div>
                    </td>
                    <td>
                      {row.batches} / {row.listings}
                      {row.skipped_duplicates > 0 && <div className="muted">+{row.skipped_duplicates} skipped</div>}
                    </td>
                    <td>
                      {row.created} / {row.updated} / {row.unchanged}
                    </td>
                    <td className={row.failed > 0 ? "text-danger fw-bold" : ""}>
                      {row.failed} <span className="muted">({formatPercent(row.failure_rate)})</span>
                    </td>
                    <td className="text-start">
                      {row.errors.length
                        ? row.errors.map((e) => (
                            <div key={e.code}>
                              <span className="mono">{e.code}</span> × {e.count}
                            </div>
                          ))
                        : "-"}
                    </td>
                    <td>{formatDate(row.last_push_at)}</td>
                    <td>
                      {quotaUsage(row.quotas?.daily_listings)}
                      <div>{quotaUsage(row.quotas?.daily_photos)}</div>
                    </td>
                    <td>
                      <div className="action-btns">
                        <Button variant="outlined" onClick={() => onShowPushes(String(row.subsidiary.id))}>
                          Pushes
                        </Button>
                        {access.canUpdate && (
                          <Button variant="outlined" onClick={() => onOpenQuotas(row.subsidiary)}>
                            Quotas
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <NoRecord />
              )}
            </tbody>
          )}
        </table>
      </div>
    </>
  );
};

export default ActivityTab;
